// ============================================================
// Datei: ShiftPrintList.jsx
// PFAD: src/components/ShiftPrintList.jsx
//
// PURPOSE:
// Wiederverwendbare React-Komponente für die druckbare und
// herunterladbare Schichtliste im Bereich "Extrawürscht".
//
// Diese Komponente:
// - lädt Schichten über GET /api/prints/shift-plan
// - zeigt eine Vorschau im Modal
// - erstellt eine HTML-Datei zum Herunterladen
// - öffnet einen sauberen Druckauszug in einem separaten Fenster
//
// ÄNDERUNGEN:
// - Druckfunktion verbessert: Es wird nicht mehr die ganze React-Seite gedruckt
// - Print öffnet jetzt einen eigenen HTML-Auszug
// - Department-Erkennung robuster gemacht
// - Dateiname sicherer gemacht
// - Daten-Fallbacks verbessert
// - Sortierung im Frontend ergänzt, falls Backend unsortiert liefert
// - bestehende Backend-URL beibehalten
// ============================================================

import React, { useEffect, useMemo, useState } from "react";
import Api from "../Api";
import "../styles/shiftPrintList.css";

const ShiftPrintList = ({ departmentName = "" }) => {
    // ============================================================
    // USER INFOS
    //
    // Wichtig:
    // - departmentName kommt vom Ressort, in dem die Komponente eingebaut ist.
    // - localStorage.department kommt vom eingeloggten Benutzer.
    // - Für normale OK-Mitglieder ist localStorage meistens korrekt.
    // - Für wiederverwendbare Komponenten ist departmentName als Fallback wichtig.
    // ============================================================
    const storedDepartment = localStorage.getItem("department") || "";
    const userDepartment = storedDepartment || departmentName || "";

    // ============================================================
    // STATE
    // ============================================================
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState("own"); // own | all
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [shifts, setShifts] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // ============================================================
    // KASSE darf "alle" wählen
    //
    // Backend prüft zusätzlich nochmals die Rechte.
    // Das Frontend zeigt den Button nur für Kasse an.
    // ============================================================
    const isKasse = useMemo(() => {
        return userDepartment?.toLowerCase() === "kasse";
    }, [userDepartment]);

    // ============================================================
    // MODAL STEUERUNG
    // ============================================================
    const openDialog = () => {
        setIsOpen(true);
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    // ============================================================
    // ESC zum Schliessen
    // ============================================================
    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeDialog();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    // ============================================================
    // HELPER:
    // Baut die URL für den Backend-Endpoint
    //
    // Backend-Endpoint:
    // GET /api/prints/shift-plan
    //
    // Da Api.js normalerweise baseURL "/api" hat,
    // verwenden wir hier nur:
    // /prints/shift-plan
    //
    // Beispiele:
    // /prints/shift-plan?department=Kasse
    // /prints/shift-plan
    // ============================================================
    const buildPrintListUrl = (selectedMode = mode) => {
        const params = new URLSearchParams();

        // --------------------------------------------------------
        // Kasse + all:
        // Kein department-Parameter.
        // Backend liefert dann alle Ressorts, falls Benutzer berechtigt ist.
        // --------------------------------------------------------
        if (isKasse && selectedMode === "all") {
            return "/prints/shift-plan";
        }

        // --------------------------------------------------------
        // Normalfall:
        // Eigenes Ressort laden.
        // --------------------------------------------------------
        if (userDepartment) {
            params.append("department", userDepartment);
        }

        const queryString = params.toString();

        return queryString
            ? `/prints/shift-plan?${queryString}`
            : "/prints/shift-plan";
    };

    // ============================================================
    // HELPER:
    // HTML escapen
    //
    // Wichtig:
    // Daten aus der Datenbank werden sicher in HTML eingesetzt.
    // ============================================================
    const escapeHtml = (value) => {
        if (value === null || value === undefined) return "";

        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    // ============================================================
    // HELPER:
    // Sichere Textausgabe
    // ============================================================
    const safeText = (value, fallback = "-") => {
        if (value === null || value === undefined) return fallback;

        const text = String(value).trim();

        return text.length > 0 ? text : fallback;
    };

    // ============================================================
    // HELPER:
    // Sichere Zahl
    // ============================================================
    const safeNumber = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return 0;
        }

        return Number(value);
    };

    // ============================================================
    // HELPER:
    // Dateiname bereinigen
    //
    // Entfernt Zeichen, die in Dateinamen Probleme machen können.
    // ============================================================
    const sanitizeFileNamePart = (value) => {
        return safeText(value, "Ressort")
            .replace(/\s+/g, "_")
            .replace(/[^\wäöüÄÖÜß-]/g, "");
    };

    // ============================================================
    // HELPER:
    // Sortierung nach Ressort, Tag und Startzeit
    //
    // Das Backend sortiert bereits im Service.
    // Diese Sortierung ist nur ein zusätzlicher Schutz fürs Frontend.
    // ============================================================
    const sortShifts = (shiftList) => {
        const dayOrder = {
            Montag: 1,
            Dienstag: 2,
            Mittwoch: 3,
            Donnerstag: 4,
            Freitag: 5,
            Samstag: 6,
            Sonntag: 7,
        };

        return [...shiftList].sort((a, b) => {
            const departmentA = safeText(a.department, "").toLowerCase();
            const departmentB = safeText(b.department, "").toLowerCase();

            if (departmentA !== departmentB) {
                return departmentA.localeCompare(departmentB);
            }

            const dayA = dayOrder[safeText(a.day, "")] || 99;
            const dayB = dayOrder[safeText(b.day, "")] || 99;

            if (dayA !== dayB) {
                return dayA - dayB;
            }

            const startA = safeText(a.startTime, "99:99");
            const startB = safeText(b.startTime, "99:99");

            return startA.localeCompare(startB);
        });
    };

    // ============================================================
    // HELPER:
    // Gruppiert die Schichten nach Ressort
    // ============================================================
    const groupShiftsByDepartment = (shiftList) => {
        const groups = {};

        for (const shift of sortShifts(shiftList || [])) {
            const department = safeText(shift.department, "Ohne Ressort");

            if (!groups[department]) {
                groups[department] = [];
            }

            groups[department].push(shift);
        }

        return groups;
    };

    // ============================================================
    // HELPER:
    // Dateiname
    // ============================================================
    const createFileName = (selectedMode = mode) => {
        if (isKasse && selectedMode === "all") {
            return "Einsatzplan_Alle_Ressorts.html";
        }

        const safeDepartment = sanitizeFileNamePart(userDepartment || departmentName || "Ressort");

        return `Einsatzplan_${safeDepartment}.html`;
    };

    // ============================================================
    // HELPER:
    // Dokumenttitel
    // ============================================================
    const createDocumentTitle = (selectedMode = mode) => {
        if (isKasse && selectedMode === "all") {
            return "Einsatzplan Alle Ressorts";
        }

        return `Einsatzplan ${safeText(userDepartment || departmentName, "Ressort")}`;
    };

    // ============================================================
    // HELPER:
    // Baut fertige HTML-Datei für Download und Druck
    // ============================================================
    const createHtmlDocument = (shiftList, selectedMode = mode) => {
        const sortedShifts = sortShifts(shiftList || []);
        const groupedByDepartment = groupShiftsByDepartment(sortedShifts);
        const createdAt = new Date().toLocaleString("de-CH");
        const documentTitle = createDocumentTitle(selectedMode);

        const departmentTablesHtml = Object.entries(groupedByDepartment)
            .map(([department, departmentShifts]) => {
                const shiftsHtml = departmentShifts
                    .map((shift) => {
                        const volunteersHtml =
                            shift.volunteers && shift.volunteers.length > 0
                                ? shift.volunteers
                                      .map((volunteer) => {
                                          const fullName =
                                              `${safeText(volunteer.firstName, "")} ${safeText(volunteer.lastName, "")}`.trim() || "-";

                                          return `
                                              <tr>
                                                  <td>${escapeHtml(fullName)}</td>
                                                  <td>${escapeHtml(safeText(volunteer.phoneNumber))}</td>
                                              </tr>
                                          `;
                                      })
                                      .join("")
                                : `
                                    <tr>
                                        <td colspan="2">Noch keine Helfer eingetragen</td>
                                    </tr>
                                  `;

                        return `
                            <div class="shift-block">
                                <div class="shift-head">
                                    <div>
                                        <strong>${escapeHtml(safeText(shift.day))}</strong>
                                        |
                                        ${escapeHtml(safeText(shift.startTime))} - ${escapeHtml(safeText(shift.endTime))}
                                    </div>
                                    <div>
                                        ${escapeHtml(safeNumber(shift.bookedShift))}
                                        /
                                        ${escapeHtml(safeNumber(shift.requestedAmountOfVolunteers))}
                                        Helfer
                                    </div>
                                </div>

                                <table>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Telefonnummer</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${volunteersHtml}
                                    </tbody>
                                </table>
                            </div>
                        `;
                    })
                    .join("");

                return `
                    <section class="department-section">
                        <h2>${escapeHtml(department)}</h2>
                        ${shiftsHtml}
                    </section>
                `;
            })
            .join("");

        return `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8" />
    <title>${escapeHtml(documentTitle)}</title>

    <style>
        body {
            font-family: Arial, Helvetica, sans-serif;
            margin: 24px;
            color: #111;
            background: #fff;
        }

        h1 {
            margin: 0 0 6px 0;
            font-size: 26px;
        }

        .meta {
            margin-bottom: 24px;
            color: #444;
            font-size: 14px;
        }

        .department-section {
            margin-bottom: 36px;
            page-break-inside: avoid;
        }

        .department-section h2 {
            margin: 0 0 14px 0;
            padding-bottom: 6px;
            border-bottom: 2px solid #222;
            font-size: 20px;
        }

        .shift-block {
            margin-bottom: 20px;
            border: 1px solid #bbb;
            border-radius: 8px;
            overflow: hidden;
            page-break-inside: avoid;
        }

        .shift-head {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            padding: 10px 12px;
            background: #f2f2f2;
            font-size: 14px;
            font-weight: bold;
        }

        table {
            width: 100%;
            border-collapse: collapse;
        }

        th,
        td {
            border: 1px solid #ccc;
            padding: 10px 12px;
            text-align: left;
            vertical-align: top;
            font-size: 14px;
        }

        th {
            background: #fafafa;
        }

        @media print {
            body {
                margin: 10mm;
            }

            .department-section,
            .shift-block {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <h1>${escapeHtml(documentTitle)}</h1>
    <div class="meta">Erstellt am: ${escapeHtml(createdAt)}</div>
    ${departmentTablesHtml || "<p>Keine Schichten gefunden.</p>"}
</body>
</html>
        `.trim();
    };

    // ============================================================
    // HELPER:
    // Startet Download
    // ============================================================
    const downloadHtmlFile = (htmlContent, fileName) => {
        const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8;" });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(downloadUrl);
    };

    // ============================================================
    // HELPER:
    // Öffnet ein separates Druckfenster
    //
    // Vorteil:
    // - Es wird nur der Einsatzplan gedruckt.
    // - Nicht die komplette React-Seite mit Navbar, Hintergrund etc.
    // ============================================================
    const printHtmlDocument = (htmlContent) => {
        const printWindow = window.open("", "_blank", "width=1000,height=800");

        if (!printWindow) {
            setError("Das Druckfenster konnte nicht geöffnet werden. Bitte Popups im Browser erlauben.");
            return;
        }

        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    };

    // ============================================================
    // API CALL
    // ============================================================
    const fetchShiftPrintList = async (selectedMode = mode) => {
        const url = buildPrintListUrl(selectedMode);
        const response = await Api.get(url);

        if (!Array.isArray(response.data)) {
            return [];
        }

        return sortShifts(response.data);
    };

    // ============================================================
    // VORSCHAU LADEN
    // ============================================================
    const loadShiftPrintList = async () => {
        if (loading) return;

        setLoading(true);
        setError("");

        try {
            const data = await fetchShiftPrintList(mode);

            setShifts(data);
            setLoaded(true);
        } catch (err) {
            console.error("Fehler beim Laden der Schicht-Druckliste:", err);

            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                (typeof err?.response?.data === "string" ? err.response.data : "") ||
                err?.message ||
                "Unbekannter Fehler";

            setError(`Die Schichtliste konnte nicht geladen werden. (${backendMessage})`);
            setShifts([]);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // DIREKT-DOWNLOAD
    // ============================================================
    const handleDirectDownload = async (selectedMode) => {
        if (loading) return;

        setLoading(true);
        setError("");
        setMode(selectedMode);

        try {
            const data = await fetchShiftPrintList(selectedMode);

            setShifts(data);
            setLoaded(true);

            if (!data || data.length === 0) {
                setError("Keine Schichten gefunden.");
                return;
            }

            const html = createHtmlDocument(data, selectedMode);
            const fileName = createFileName(selectedMode);

            downloadHtmlFile(html, fileName);
        } catch (err) {
            console.error("Fehler beim direkten Download der Schichtliste:", err);

            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                (typeof err?.response?.data === "string" ? err.response.data : "") ||
                err?.message ||
                "Unbekannter Fehler";

            setError(`Der Einsatzplan konnte nicht heruntergeladen werden. (${backendMessage})`);
            setShifts([]);
            setLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // DOWNLOAD AUS GELADENEN DATEN
    // ============================================================
    const handleDownloadCurrentList = () => {
        if (!shifts || shifts.length === 0) {
            setError("Bitte zuerst eine Schichtliste laden.");
            return;
        }

        const html = createHtmlDocument(shifts, mode);
        const fileName = createFileName(mode);

        downloadHtmlFile(html, fileName);
    };

    // ============================================================
    // DRUCKEN AUS GELADENEN DATEN
    // ============================================================
    const handlePrint = () => {
        if (!shifts || shifts.length === 0) {
            setError("Bitte zuerst eine Schichtliste laden.");
            return;
        }

        const html = createHtmlDocument(shifts, mode);

        printHtmlDocument(html);
    };

    // ============================================================
    // GRUPPIERUNG FÜR MODAL
    // ============================================================
    const groupedByDepartment = useMemo(() => {
        return groupShiftsByDepartment(shifts);
    }, [shifts]);

    return (
        <>
            <button
                type="button"
                className="shift-print-launcher"
                onClick={openDialog}
            >
                <span className="shift-print-launcher-icon">🖨️</span>

                <span className="shift-print-launcher-text">
                    <span className="shift-print-launcher-title">
                        Auszug Schichtplan
                    </span>
                </span>
            </button>

            {isOpen && (
                <div className="shift-print-modal-overlay" onClick={closeDialog}>
                    <div
                        className="shift-print-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="shift-print-modal-header no-print">
                            <div>
                                <h3>Schichtliste drucken</h3>
                                <p>
                                    Liste aller Schichten mit eingetragenen Helfern und Telefonnummern.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="shift-print-close-btn"
                                onClick={closeDialog}
                                aria-label="Dialog schliessen"
                            >
                                ×
                            </button>
                        </div>

                        <div className="shift-print-list-actions no-print">
                            {isKasse && (
                                <div className="shift-print-list-mode-switch">
                                    <button
                                        type="button"
                                        className={mode === "own" ? "active" : ""}
                                        onClick={() => handleDirectDownload("own")}
                                        disabled={loading}
                                    >
                                        {loading && mode === "own" ? "Lade..." : "Nur Kasse"}
                                    </button>

                                    <button
                                        type="button"
                                        className={mode === "all" ? "active" : ""}
                                        onClick={() => handleDirectDownload("all")}
                                        disabled={loading}
                                    >
                                        {loading && mode === "all" ? "Lade..." : "Alle Ressorts"}
                                    </button>
                                </div>
                            )}

                            {!isKasse && (
                                <div className="shift-print-list-mode-switch">
                                    <button
                                        type="button"
                                        className="active"
                                        onClick={() => handleDirectDownload("own")}
                                        disabled={loading}
                                    >
                                        {loading ? "Lade..." : `${safeText(userDepartment || departmentName, "Ressort")} herunterladen`}
                                    </button>
                                </div>
                            )}

                            <div className="shift-print-list-buttons">
                                <button
                                    type="button"
                                    className="shift-print-load-btn"
                                    onClick={loadShiftPrintList}
                                    disabled={loading}
                                >
                                    {loading ? "Lade..." : "Schichtliste laden"}
                                </button>

                                <button
                                    type="button"
                                    className="shift-print-print-btn"
                                    onClick={handleDownloadCurrentList}
                                    disabled={!loaded || shifts.length === 0 || loading}
                                >
                                    Download
                                </button>

                                <button
                                    type="button"
                                    className="shift-print-print-btn"
                                    onClick={handlePrint}
                                    disabled={!loaded || shifts.length === 0 || loading}
                                >
                                    Drucken
                                </button>
                            </div>
                        </div>

                        {error && <div className="shift-print-error">{error}</div>}

                        {loaded && !loading && shifts.length === 0 && !error && (
                            <div className="shift-print-empty">
                                Keine Schichten gefunden.
                            </div>
                        )}

                        {loaded && shifts.length > 0 && (
                            <div className="shift-print-content">
                                {Object.entries(groupedByDepartment).map(([department, departmentShifts]) => (
                                    <div className="shift-print-department-block" key={department}>
                                        <h4>{department}</h4>

                                        {departmentShifts.map((shift) => (
                                            <div className="shift-print-shift-card" key={shift.shiftId}>
                                                <div className="shift-print-shift-head">
                                                    <div className="shift-print-shift-title">
                                                        <strong>{safeText(shift.day)}</strong>
                                                        {" | "}
                                                        {safeText(shift.startTime)} - {safeText(shift.endTime)}
                                                    </div>

                                                    <div className="shift-print-shift-meta">
                                                        {safeNumber(shift.bookedShift)}
                                                        {" / "}
                                                        {safeNumber(shift.requestedAmountOfVolunteers)}
                                                        {" Helfer"}
                                                    </div>
                                                </div>

                                                <div className="shift-print-volunteer-list">
                                                    {shift.volunteers && shift.volunteers.length > 0 ? (
                                                        shift.volunteers.map((volunteer, index) => (
                                                            <div
                                                                className="shift-print-volunteer-row"
                                                                key={`${shift.shiftId}-${volunteer.volunteerId || index}`}
                                                            >
                                                                <span className="shift-print-volunteer-name">
                                                                    {safeText(
                                                                        `${safeText(volunteer.firstName, "")} ${safeText(volunteer.lastName, "")}`.trim()
                                                                    )}
                                                                </span>

                                                                <span className="shift-print-volunteer-phone">
                                                                    {safeText(volunteer.phoneNumber)}
                                                                </span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="shift-print-no-volunteers">
                                                            Noch keine Helfer eingetragen
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ShiftPrintList;